import { useEffect, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Cpu,
  Database,
  ExternalLink,
  HardDrive,
  HelpCircle,
  MemoryStick,
  RefreshCw,
  Server,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  AlertDialogBody,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { BusyOverlay } from "@/components/common/BusyOverlay";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PermissionGate } from "@/components/common/PermissionGate";
import {
  useResolveSpecMismatch,
  useResourceSpecMismatch,
} from "@/hooks/useResourceQueries";
import { cn, formatDateTime } from "@/lib/utils";
import type { SpecMismatchField } from "@/types/admin";

interface SpecMismatchDialogProps {
  /** The VM to compare. */
  resourceId: string;
  /** Name shown in the heading, so the dialog identifies the VM before the answer arrives. */
  resourceName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Whether the update covers the whole page while it runs. The resource table passes `false` and
   * covers its own Card instead, driven by `onPendingChange`.
   */
  pageOverlay?: boolean;
  /** Mirrors whether the update is running, for a caller that shows its own busy state. */
  onPendingChange?: (pending: boolean) => void;
}

/** Renders one configuration figure with its unit, or an em dash when the side holds nothing. */
function formatValue(value: number | null, unit: string | null): string {
  if (value === null || value === undefined) return "—";
  return unit ? `${value} ${unit}` : `${value}`;
}

/** The icon that makes each row recognisable at a glance. */
const FIELD_ICONS: Record<string, LucideIcon> = {
  cores: Cpu,
  memory: MemoryStick,
  disk: HardDrive,
};

/**
 * The gap as a signed figure — `+2048 MB` — kept short enough to sit beside the badge instead of
 * wrapping under it. The long form is carried on `title` for anyone who wants it spelled out.
 */
function describeDifference(
  field: SpecMismatchField,
): { short: string; long: string } | null {
  if (
    field.status !== "Mismatch" ||
    field.wiCloudValue === null ||
    field.providerValue === null
  ) {
    return null;
  }

  const delta = field.providerValue - field.wiCloudValue;
  const unit = field.unit ? ` ${field.unit}` : "";
  return {
    short: `${delta > 0 ? "+" : "−"}${Math.abs(delta)}${unit}`,
    long: `${Math.abs(delta)}${unit} ${delta > 0 ? "more" : "less"} than recorded`,
  };
}

function statusBadge(field: SpecMismatchField) {
  switch (field.status) {
    case "Mismatch":
      return <Badge variant="warning">Differs</Badge>;
    case "MissingLocal":
      return <Badge variant="warning">Not recorded</Badge>;
    case "MissingProvider":
      return <Badge variant="secondary">Not reported</Badge>;
    default:
      return <Badge variant="outline">Match</Badge>;
  }
}

const BANNER_TONES = {
  ok: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  warning:
    "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400",
  error: "border-destructive/40 bg-destructive/10 text-destructive",
} as const;

/**
 * One-line verdict strip. A full `<Alert>` spent a title and a paragraph saying what fits on a
 * single row, which pushed the table — the thing the operator opened this for — below the fold.
 */
function StatusBanner({
  tone,
  icon: Icon,
  children,
}: {
  tone: keyof typeof BANNER_TONES;
  icon: LucideIcon;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex items-start gap-2 rounded-lg border px-3 py-2 text-sm",
        BANNER_TONES[tone],
      )}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <span className="min-w-0">{children}</span>
    </div>
  );
}

/** Column heading: the source on top, where it comes from underneath, both on one line each. */
function SourceHead({
  icon: Icon,
  name,
  origin,
}: {
  icon: LucideIcon;
  name: string;
  origin: string;
}) {
  return (
    <span className="block whitespace-nowrap">
      <span className="flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5" />
        {name}
      </span>
      <span className="block text-[11px] font-normal text-muted-foreground">
        {origin}
      </span>
    </span>
  );
}

/**
 * The short answer to "what does Update actually change", opened in place.
 *
 * The one misreading this screen invites is that it resizes the VM. It does not — the hardware is
 * already what it is, and only WiCloud's record of it moves. That, and the fact the price does not
 * follow, are the two things worth saying before the operator presses. Everything else is in the
 * full guide.
 */
function HelpPopover() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" size="sm">
          <HelpCircle className="mr-2 h-4 w-4" />
          How this works
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="top"
        className="w-80 space-y-3 text-sm"
      >
        <p className="text-muted-foreground">
          WiCloud stores each VM&rsquo;s vCPU, memory and disk on its own
          record, and that is what the customer is shown. If the machine is
          resized on the hypervisor, nothing re-reads those figures — so the two
          drift apart and nobody notices.
        </p>

        <div className="space-y-1.5">
          <p className="font-medium">Update copies one way</p>
          <p className="text-muted-foreground">
            Infrastructure → WiCloud, for the rows marked Differs or Not
            recorded. Values are re-read at that moment, so they can differ from
            what is on screen.
          </p>
        </div>

        <div className="space-y-1.5">
          <p className="font-medium">It never</p>
          <ul className="space-y-1 text-muted-foreground">
            <li>· resizes or restarts the VM</li>
            <li>· changes the price, the billing cycle or the order</li>
            <li>· writes a value the infrastructure did not report</li>
          </ul>
        </div>

        <Button variant="outline" size="sm" asChild className="w-full">
          <a href="/docs/spec-mismatch.html" target="_blank" rel="noreferrer">
            Full guide
            <ExternalLink className="ml-2 h-3.5 w-3.5" />
          </a>
        </Button>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Side-by-side comparison of one VM's configuration: what WiCloud has stored on the resource row,
 * and what a live call to the WiService (n8n) webhook says the hypervisor actually runs — with a
 * way to copy the second onto the first when they have come apart.
 *
 * Two things it deliberately does not do. It never changes the VM: the hardware is already what it
 * is, and this corrects WiCloud's record of it. And it never touches pricing — what a customer is
 * charged stays a separate, deliberate decision.
 *
 * The provider is asked only while the dialog is open, and never from cache: a stale answer is
 * precisely what this comparison exists to expose.
 */
export function SpecMismatchDialog({
  resourceId,
  resourceName,
  open,
  onOpenChange,
  pageOverlay = true,
  onPendingChange,
}: SpecMismatchDialogProps) {
  const {
    data: mismatch,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useResourceSpecMismatch(resourceId, open);

  const resolveMismatch = useResolveSpecMismatch();

  // Mirrored for a caller that covers its own area instead of the page (see `pageOverlay`), and
  // released on unmount too, so a dialog that goes away cannot leave that caller's overlay up.
  useEffect(() => {
    onPendingChange?.(resolveMismatch.isPending);
  }, [resolveMismatch.isPending, onPendingChange]);
  useEffect(() => () => onPendingChange?.(false), [onPendingChange]);
  // Controlled so the confirm can outlive the click that submits it — see the action below.
  const [confirmOpen, setConfirmOpen] = useState(false);

  const appliableFields =
    mismatch?.fields.filter((field) => field.canApply) ?? [];
  const canApply = !!mismatch?.providerReachable && appliableFields.length > 0;
  const displayName = mismatch?.resourceName ?? resourceName;

  return (
    <Dialog
      open={open}
      // Held while the update is out. The comparison it writes back lands in this dialog, and a
      // Re-check started after closing and reopening could land later and overwrite it with an
      // older answer.
      onOpenChange={(next) => {
        if (next || !resolveMismatch.isPending) onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Update mismatched configuration
            {displayName ? ` · ${displayName}` : ""}
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-3">
          <DialogDescription>
            Updating copies the Infrastructure column onto WiCloud's record. The
            VM is not resized and pricing does not change.
          </DialogDescription>
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          ) : error && !mismatch ? (
            <StatusBanner tone="error" icon={AlertTriangle}>
              {error instanceof Error
                ? error.message
                : "The comparison request failed."}
            </StatusBanner>
          ) : !mismatch ? null : (
            <>
              {/* A failed Re-check keeps the answer already on screen: TanStack holds `data` when a
                  refetch fails, and throwing the table away for a provider blip would leave the
                  operator nothing to decide on. */}
              {error && (
                <StatusBanner tone="warning" icon={AlertTriangle}>
                  Re-check failed — showing the previous answer.{" "}
                  {error instanceof Error ? error.message : ""}
                </StatusBanner>
              )}
              {/* An unreachable provider is a normal answer, not a failure: the table below still
                  shows what WiCloud holds, which is half of what the operator came to see. */}
              {!mismatch.providerReachable && (
                <StatusBanner tone="warning" icon={AlertTriangle}>
                  Nothing to compare against — {mismatch.providerError}
                </StatusBanner>
              )}

              {mismatch.providerReachable && !mismatch.hasMismatch && (
                <StatusBanner tone="ok" icon={CheckCircle2}>
                  Configuration matches — every recorded value is the one the
                  infrastructure reports.
                </StatusBanner>
              )}

              {mismatch.providerReachable && mismatch.hasMismatch && (
                <StatusBanner tone="warning" icon={AlertTriangle}>
                  Configuration does not match — the values marked "Differs" are
                  shown incorrectly to this resource's owner.
                </StatusBanner>
              )}

              <div className="overflow-x-auto rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="w-[30%]">Value</TableHead>
                      <TableHead className="w-[22%]">
                        <SourceHead
                          icon={Database}
                          name="WiCloud"
                          origin="stored"
                        />
                      </TableHead>
                      <TableHead className="w-[22%]">
                        <SourceHead
                          icon={Server}
                          name="Infrastructure"
                          origin="live · WiService"
                        />
                      </TableHead>
                      <TableHead className="w-[26%] text-right">
                        Result
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mismatch.fields.map((field) => {
                      const Icon = FIELD_ICONS[field.key];
                      const difference = describeDifference(field);
                      const differs = field.status === "Mismatch";

                      return (
                        <TableRow
                          key={field.key}
                          className={cn(
                            differs && "bg-amber-50/60 dark:bg-amber-500/10",
                          )}
                        >
                          <TableCell className="font-medium">
                            <span className="flex items-center gap-2">
                              {Icon && (
                                <Icon className="h-4 w-4 text-muted-foreground" />
                              )}
                              {field.label}
                            </span>
                          </TableCell>
                          <TableCell
                            className={cn(
                              "whitespace-nowrap tabular-nums",
                              differs && "text-muted-foreground line-through",
                            )}
                          >
                            {formatValue(field.wiCloudValue, field.unit)}
                          </TableCell>
                          <TableCell
                            className={cn(
                              "whitespace-nowrap tabular-nums",
                              differs &&
                                "font-semibold text-amber-700 dark:text-amber-400",
                            )}
                          >
                            {formatValue(field.providerValue, field.unit)}
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="flex items-center justify-end gap-2">
                              {difference && (
                                <span
                                  className="text-xs tabular-nums text-muted-foreground"
                                  title={difference.long}
                                >
                                  {difference.short}
                                </span>
                              )}
                              {statusBadge(field)}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                <span>Checked {formatDateTime(mismatch.checkedAt)}</span>
                {mismatch.vmUuid && (
                  <>
                    <span aria-hidden>·</span>
                    {/* Full UUID on hover — at this size it is an identifier to copy, not to read. */}
                    <span className="font-mono" title={mismatch.vmUuid}>
                      VM {mismatch.vmUuid.slice(0, 8)}…
                    </span>
                  </>
                )}
                {mismatch.providerPowerState && (
                  <>
                    <span aria-hidden>·</span>
                    <span>{mismatch.providerPowerState}</span>
                  </>
                )}
              </p>
            </>
          )}
        </DialogBody>

        <DialogFooter className="gap-2 sm:justify-between">
          {/* Grouped so the footer keeps its two-ends layout: everything informational on the left,
              the write on the right. */}
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching || resolveMismatch.isPending}
            >
              {isFetching ? (
                <Spinner className="h-4 w-4" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Re-check
            </Button>

            <HelpPopover />
          </div>

          {/* SuperAdmin-only, mirroring the [AdminRole] gate on the resolve endpoint. */}
          <PermissionGate roles="SuperAdmin">
            <AlertDialog
              open={confirmOpen}
              onOpenChange={(next) => {
                if (next || !resolveMismatch.isPending) setConfirmOpen(next);
              }}
            >
              <AlertDialogTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  disabled={!canApply || resolveMismatch.isPending}
                  title={
                    canApply
                      ? undefined
                      : "Nothing to update — the stored values already match the infrastructure"
                  }
                >
                  {resolveMismatch.isPending ? (
                    <Spinner className="h-4 w-4" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  Update WiCloud to match
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent
                onEscapeKeyDown={(event) => {
                  if (resolveMismatch.isPending) event.preventDefault();
                }}
              >
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    Update the stored configuration?
                  </AlertDialogTitle>
                </AlertDialogHeader>
                <AlertDialogBody>
                  <AlertDialogDescription asChild>
                    <div className="space-y-3">
                      <p>
                        {displayName ?? "This resource"} will be recorded as:
                      </p>
                      <ul className="space-y-1">
                        {appliableFields.map((field) => (
                          <li key={field.key} className="text-sm">
                            <span className="font-medium">{field.label}</span>:{" "}
                            {formatValue(field.wiCloudValue, field.unit)} →{" "}
                            <span className="font-semibold">
                              {formatValue(field.providerValue, field.unit)}
                            </span>
                          </li>
                        ))}
                      </ul>
                      <p>
                        The VM, its price and its billing cycle are untouched.
                        Values are re-read at that moment, so they may differ
                        from the ones above if the infrastructure changed again.
                      </p>
                    </div>
                  </AlertDialogDescription>
                </AlertDialogBody>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={resolveMismatch.isPending}>
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    disabled={resolveMismatch.isPending}
                    onClick={(event) => {
                      // Radix closes on click. Held open until the write and its re-read have
                      // landed, so the confirm is still up when the new comparison replaces the
                      // table behind it rather than vanishing on the click.
                      event.preventDefault();
                      resolveMismatch.mutate(
                        { id: resourceId },
                        { onSettled: () => setConfirmOpen(false) },
                      );
                    }}
                  >
                    {resolveMismatch.isPending && <Spinner className="mr-2" />}
                    Update configuration
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </PermissionGate>
        </DialogFooter>
        <BusyOverlay
          open={resolveMismatch.isPending && pageOverlay}
          label="Updating the stored configuration…"
        />
      </DialogContent>
    </Dialog>
  );
}
